import { useState } from "react";
import { motion } from "framer-motion";
import { Pill, AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { api } from "../api/client";

type Rec = {
  vitamin_id: string;
  name: string;
  score: number;
  confidence: number;
  nhs_rda: string;
  reasoning: string[];
  personalised_for?: string[];
  food_sources?: string[];
  nhs_reference?: string;
};

const SEASONS = ["spring", "summer", "autumn", "winter"];
const ETHNICITIES = [
  { v: "white_british", l: "White British" },
  { v: "south_asian", l: "South Asian" },
  { v: "black_african_caribbean", l: "Black African/Caribbean" },
  { v: "mixed", l: "Mixed" },
  { v: "other", l: "Other" },
];
const REGIONS = [
  "london", "south_east", "south_west", "east", "east_midlands", "west_midlands",
  "north_west", "north_east", "yorkshire", "scotland", "wales", "northern_ireland",
];
const DIETS = ["omnivore", "vegetarian", "vegan", "pescatarian"];
const ACTIVITY_LEVELS = ["inactive", "low", "moderate", "high"];
const SMOKING = ["never", "former", "current"];

export default function VitaminPage() {
  const [profile, setProfile] = useState({
    age: 42,
    gender: "female",
    ethnicity: "south_asian",
    region: "london",
    bmi: 26.5,
    diet_type: "vegetarian",
    season: "winter",
    smoking_status: "never",
    alcohol_weekly_units: 0,
    physical_activity: "moderate",
    pregnant_or_breastfeeding: false,
    medications: "",
    conditions: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    recommendations: Rec[];
    profile_summary: string;
    disclaimer: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        age: profile.age,
        // Backend still accepts `sex`; we send both for forward-compat
        sex: profile.gender,
        gender: profile.gender,
        ethnicity: profile.ethnicity,
        region: profile.region,
        bmi: profile.bmi,
        diet_type: profile.diet_type,
        season: profile.season,
        smoking_status: profile.smoking_status,
        alcohol_weekly_units: profile.alcohol_weekly_units,
        physical_activity: profile.physical_activity,
        pregnant_or_breastfeeding: profile.pregnant_or_breastfeeding,
        medications: profile.medications.split(",").map((s) => s.trim()).filter(Boolean),
        conditions: profile.conditions.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const res = await api.vitaminRecommend(payload);
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const showPregnancy =
    profile.gender === "female" && profile.age >= 15 && profile.age <= 55;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Pill className="text-nhs-blue" /> Vitamin Recommender
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Your plan, built around you — NHS and SACN-aligned. Not medical advice.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="font-semibold mb-4">Your profile</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Age</label>
                <input
                  type="number"
                  className="input"
                  value={profile.age}
                  min={0}
                  max={120}
                  onChange={(e) =>
                    setProfile({ ...profile, age: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <label className="label">Gender</label>
                <select
                  className="input"
                  value={profile.gender}
                  onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">Ethnicity</label>
              <select
                className="input"
                value={profile.ethnicity}
                onChange={(e) => setProfile({ ...profile, ethnicity: e.target.value })}
              >
                {ETHNICITIES.map((e) => (
                  <option key={e.v} value={e.v}>
                    {e.l}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">UK Region</label>
                <select
                  className="input"
                  value={profile.region}
                  onChange={(e) => setProfile({ ...profile, region: e.target.value })}
                >
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">BMI</label>
                <input
                  type="number"
                  step="0.1"
                  className="input"
                  value={profile.bmi}
                  min={10}
                  max={70}
                  onChange={(e) =>
                    setProfile({ ...profile, bmi: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Diet</label>
                <select
                  className="input"
                  value={profile.diet_type}
                  onChange={(e) => setProfile({ ...profile, diet_type: e.target.value })}
                >
                  {DIETS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Season</label>
                <select
                  className="input"
                  value={profile.season}
                  onChange={(e) => setProfile({ ...profile, season: e.target.value })}
                >
                  {SEASONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Smoking</label>
                <select
                  className="input"
                  value={profile.smoking_status}
                  onChange={(e) =>
                    setProfile({ ...profile, smoking_status: e.target.value })
                  }
                >
                  {SMOKING.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Activity level</label>
                <select
                  className="input"
                  value={profile.physical_activity}
                  onChange={(e) =>
                    setProfile({ ...profile, physical_activity: e.target.value })
                  }
                >
                  {ACTIVITY_LEVELS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Alcohol (units per week)</label>
              <input
                type="number"
                step="1"
                min={0}
                max={100}
                className="input"
                value={profile.alcohol_weekly_units}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    alcohol_weekly_units: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            {showPregnancy && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={profile.pregnant_or_breastfeeding}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      pregnant_or_breastfeeding: e.target.checked,
                    })
                  }
                />
                Pregnant or breastfeeding
              </label>
            )}
            <div>
              <label className="label">Medications (comma-separated)</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., metformin, levothyroxine, statin, ppi"
                value={profile.medications}
                onChange={(e) => setProfile({ ...profile, medications: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Conditions (comma-separated)</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., coeliac, ibd, ckd, osteoporosis"
                value={profile.conditions}
                onChange={(e) => setProfile({ ...profile, conditions: e.target.value })}
              />
            </div>
            <button onClick={submit} disabled={loading} className="btn-primary w-full">
              {loading ? <Loader2 className="animate-spin inline mr-2" size={16} /> : null}
              Get My Personalised Plan
            </button>
          </div>
        </div>

        <div className="card">
          <h2 className="font-semibold mb-4">Recommendations</h2>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-3 flex gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}
          {!result && !error && (
            <p className="text-sm text-gray-500">
              Complete the profile and click Get My Personalised Plan.
            </p>
          )}
          {result && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <p className="text-xs text-gray-500 italic">{result.profile_summary}</p>
              {result.recommendations.length === 0 && (
                <p className="text-sm text-gray-600">
                  No specific recommendations for your profile. A balanced UK diet should
                  meet your needs — consult your GP for personalised advice.
                </p>
              )}
              {result.recommendations.map((r, i) => (
                <div key={r.vitamin_id} className="border border-gray-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">
                      {i + 1}. {r.name}
                    </span>
                    <span className="text-xs bg-nhs-blue text-white px-2 py-0.5 rounded">
                      Score {r.score.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">NHS RDA: {r.nhs_rda}</p>
                  {r.personalised_for && r.personalised_for.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {r.personalised_for.map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full flex items-center gap-1"
                        >
                          <Sparkles size={10} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <ul className="text-xs text-gray-700 list-disc list-inside space-y-0.5">
                    {r.reasoning.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                  {r.food_sources && r.food_sources.length > 0 && (
                    <div className="mt-2 text-xs text-gray-700">
                      <span className="font-medium">UK food sources: </span>
                      {r.food_sources.join(", ")}
                    </div>
                  )}
                  {r.nhs_reference && (
                    <a
                      href={r.nhs_reference}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-nhs-blue hover:underline mt-2 inline-block"
                    >
                      NHS reference →
                    </a>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 italic border-t pt-2">
                {result.disclaimer}
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
